import { toPlainText } from '@portabletext/react';
import { clickPurchaseIntentEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import { useSetAtom } from 'jotai';
import { usePageContext } from 'processes/Page/PageContext';
import { useEffect, useRef } from 'react';
import { HsaFsaAvailability } from 'shared/components/HSA-FSA/HsaFsaAvailability';
import {
  Button,
  Typography,
  getTypographyDefaultColor,
  Space,
  Col,
  Container,
  CheckmarkIcon
} from '@ag1/ui';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { useStoreValue } from 'state/hooks/useStore';
import { useUnfortunateWayToAppendCurrency } from 'state/hooks/useUnfortunateWayToAppendCurrency';
import { OfferV2 as OfferV2Props } from 'types/studio/studio';
import { offerSectionAtom } from '../../../state/atoms/offerSectionAtom';
import { useGoToOffersV2 } from '../../hooks/useGoToOffersV2';
import CustomPortableText from '../PortableText/PortableText';
import { StudioHotspotImageV2 } from '../StudioHotspotImage/StudioHotspotImage';
import styles from './OfferV2.module.scss';
import { CommonSectionProps } from '../types';

type Props = OfferV2Props &
  CommonSectionProps & {
    _key: string;
  };

const OfferV2 = (props: Props) => {
  const {
    _type: sectionType,
    _key: key,
    offerImage,
    headline,
    subtitle,
    offerItems,
    theme,
    oldThemeButton,
    offerButton,
    additionalLinkSwitch,
    additionalLink,
    fullWidthButton,
    hoverColor,
    guaranteeText,
    secondCaption,
    contentPosition = 'left',
    visibleOnPageLoad
  } = props;
  const { disablePrimaryCTAButtons } = usePageContext();
  const setOfferSectionAtom = useSetAtom(offerSectionAtom);

  const sectionRef = useRef<HTMLDivElement>(null);
  const store = useStoreValue();

  useEffect(() => {
    if (sectionRef.current) {
      setOfferSectionAtom(sectionRef.current);
    }
  }, [setOfferSectionAtom, sectionRef]);

  const { goToOffer } = useGoToOffersV2({
    withScroll: false,
    bundleUrl: offerButton?.bundleUrl,
    buttonLink: offerButton?.buttonLink
  });

  const handleButtonClick = () => {
    if (disablePrimaryCTAButtons) {
      return;
    }
    const buttonText = offerButton?.baseButton?.portableText
      ? toPlainText(offerButton.baseButton.portableText)
      : '';
    clickPurchaseIntentEvent({
      store,
      clickText: buttonText,
      location: sectionType,
      url: offerButton?.bundleUrl || offerButton?.buttonLink
    });
    goToOffer();
  };

  const { conditionallyAppendUSDtoParsedString } = useUnfortunateWayToAppendCurrency();

  return (
    <div
      className={clsx(
        styles.root,
        contentPosition === 'left' && styles.reverse,
        getThemeCSSClass(theme)
      )}
      id={key}>
      {offerImage && (
        <Col className={styles.col} span={{ xxs: 12, m: 6 }}>
          <StudioHotspotImageV2
            className={clsx(
              styles.imageCont,
              contentPosition === 'left' ? styles.imageLeft : styles.imageRight
            )}
            image={offerImage}
            sizes={{ xxs: 12, m: 6 }}
            eagerLoading={visibleOnPageLoad}
          />
        </Col>
      )}
      <Col
        ref={sectionRef}
        className={clsx(
          styles.col,
          styles.colInfo,
          contentPosition === 'left' ? styles.colInfoLeft : styles.colInfoRight
        )}
        span={{ xxs: 12, m: 6 }}>
        <Container
          className={clsx(contentPosition === 'left' ? styles.clearRight : styles.clearLeft)}>
          <div className={styles.content}>
            <div className={styles.header} id="offers">
              <Typography
                variant="headlineSans"
                size="2"
                component="h2"
                color={getTypographyDefaultColor(theme)}
                noPadding>
                {headline}
              </Typography>
              {subtitle && (
                <Space top={8}>
                  <Typography
                    variant="subtitle"
                    size="1"
                    noPadding
                    color={getTypographyDefaultColor(theme)}
                    className={styles.subtitle}
                    weight="400">
                    {subtitle}
                  </Typography>
                </Space>
              )}
            </div>
            {offerItems && (
              <div className={styles.list}>
                {offerItems.map((item) => (
                  <div key={item} className={styles.item}>
                    <Space right={12}>
                      <div className={styles.itemIcon}>
                        <CheckmarkIcon />
                      </div>
                    </Space>
                    <Typography
                      color={getTypographyDefaultColor(theme)}
                      variant="body"
                      size="2"
                      className={styles.itemLabel}>
                      {conditionallyAppendUSDtoParsedString(item)}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
            <Space top={{ xxs: offerItems ? 0 : 24, s: offerItems ? 0 : 32 }}>
              <div className={styles.buttonWrapper}>
                {offerButton && offerButton.baseButton && (
                  <Button
                    size="medium"
                    theme={oldThemeButton ? 'old' : theme}
                    variant={offerButton.baseButton.variant}
                    fluid
                    rounded
                    icon={
                      offerButton.baseButton.showIcon && offerButton.baseButton.iconOptions?.icon
                    }
                    iconPosition={offerButton.baseButton.iconOptions?.position}
                    className={clsx([
                      styles.button,
                      {
                        [styles.dark]: hoverColor === 'dark',
                        [styles.button_fullWidth]: fullWidthButton
                      }
                    ])}
                    data-gtm-skip-global
                    onClick={handleButtonClick}
                    disabled={disablePrimaryCTAButtons}>
                    {offerButton.baseButton.portableText && (
                      <CustomPortableText value={offerButton.baseButton.portableText} />
                    )}
                  </Button>
                )}
                {additionalLinkSwitch && additionalLink && (
                  <Button
                    size="medium"
                    theme={oldThemeButton ? 'old' : theme}
                    rounded
                    fluid
                    className={styles.button}
                    href={additionalLink?.btnLink?.external || ''}
                    icon={additionalLink?.showIcon && additionalLink?.iconOptions?.icon}
                    iconPosition={additionalLink?.iconOptions?.position || 'right'}
                    variant={additionalLink?.variant}>
                    <CustomPortableText
                      value={additionalLink.portableText ? additionalLink.portableText : []}
                    />
                  </Button>
                )}
              </div>
              {offerItems && <HsaFsaAvailability sourcePage="primaryLander" />}
            </Space>

            {(secondCaption || guaranteeText) && (
              <div className={`${styles.captions} ${secondCaption ? styles.marginAuto : ''}`}>
                {guaranteeText && (
                  <div className={styles.guarantee}>
                    <Space right={12} bottom={2} className={styles.guaranteeIcon}>
                      <CheckmarkIcon size={12} />
                    </Space>
                    <Typography
                      color={getTypographyDefaultColor(theme)}
                      variant="portalCaption"
                      size="1"
                      noPadding>
                      {guaranteeText}
                    </Typography>
                  </div>
                )}
                {secondCaption && (
                  <div
                    className={clsx(
                      styles.guarantee,
                      styles.secondCaption,
                      !guaranteeText && styles.noMargin
                    )}>
                    <Space right={12} bottom={2} className={styles.guaranteeIcon}>
                      <CheckmarkIcon size={12} />
                    </Space>
                    <Typography
                      color={getTypographyDefaultColor(theme)}
                      variant="portalCaption"
                      size="1"
                      noPadding>
                      {secondCaption}
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </div>
        </Container>
      </Col>
    </div>
  );
};

export default OfferV2;
